import emailjs from '@emailjs/browser';
import { ChevronDown, ChevronUp, Send, Copy, Check } from 'lucide-react';
import React, { useState, useMemo } from 'react';
import { emailConfig } from '@/config/emailjs-config';
import { logger } from '@/lib/logger';
import { useApiDebugStore } from '@/stores/apiDebugStore';
import { useConsoleDebugStore } from '@/stores/consoleDebugStore';

interface DiagnosticLogsProps {
  errorContext?: string;
}

export const DiagnosticLogs: React.FC<DiagnosticLogsProps> = ({ errorContext }) => {
  const [showLogs, setShowLogs] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const apiCalls = useApiDebugStore((s) => s.calls);
  const consoleEntries = useConsoleDebugStore((s) => s.entries);

  const logSummary = useMemo(() => {
    const recentErrors = consoleEntries
      .filter((e) => e.level === 'error' || e.level === 'warn')
      .slice(0, 20)
      .map((e) => ({
        level: e.level,
        message: e.message,
        time: e.timestamp.toISOString(),
        ...(e.details?.length ? { details: e.details } : {}),
      }));

    const recentApiErrors = apiCalls
      .filter((c) => c.status && c.status >= 400)
      .slice(0, 10)
      .map((c) => ({
        url: c.url,
        method: c.method,
        status: c.status,
        error: c.error,
        time: c.timestamp.toISOString(),
      }));

    return {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      error: errorContext || 'Unknown error',
      consoleErrors: recentErrors,
      apiErrors: recentApiErrors,
    };
  }, [apiCalls, consoleEntries, errorContext]);

  const logText = useMemo(() => JSON.stringify(logSummary, null, 2), [logSummary]);

  const handleCopyLogs = async () => {
    try {
      await navigator.clipboard.writeText(logText);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      // fallback silent
    }
  };

  const handleSendLogs = async () => {
    setIsSending(true);
    try {
      const templateParams = {
        from_name: 'Error Report (Auto)',
        from_email: 'noreply@pmxhealth.com',
        to_email: 'help@pmxhealth.com',
        name: 'Error Report',
        email: 'noreply@pmxhealth.com',
        support_type: 'Technical Support - Error Log',
        message: `Error: ${errorContext || 'Unknown'}\n\nURL: ${window.location.href}\nTime: ${new Date().toISOString()}\n\n--- Logs ---\n${logText}`,
        reply_to: 'help@pmxhealth.com',
      };

      const response = await emailjs.send(
        emailConfig.serviceId,
        emailConfig.templateId,
        templateParams,
        emailConfig.publicKey
      );

      if (response.text === 'OK') {
        setIsSent(true);
      }
    } catch (err) {
      logger.error('Failed to send error logs:', err);
    } finally {
      setIsSending(false);
    }
  };

  const errorCount = consoleEntries.filter((e) => e.level === 'error' || e.level === 'warn').length;
  const failedApiCount = apiCalls.filter((c) => c.status && c.status >= 400).length;
  const totalIssues = errorCount + failedApiCount;

  return (
    <div className="pt-1">
      <div className="border-t border-[hsl(var(--border))] pt-4">
        <button
          onClick={() => setShowLogs((prev) => !prev)}
          className="flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
        >
          {showLogs ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          View diagnostic logs
          {totalIssues > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-[10px] font-medium bg-[hsl(var(--error)/0.08)] text-[hsl(var(--error))] rounded-full min-w-[1.25rem] text-center">
              {totalIssues}
            </span>
          )}
        </button>

        {showLogs && (
          <div className="mt-3 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
            <pre className="text-[11px] leading-relaxed bg-[hsl(var(--secondary))] p-4 rounded-xl overflow-auto max-h-52 whitespace-pre-wrap break-all text-[hsl(var(--muted-foreground))] font-mono border border-[hsl(var(--border))]">
              {logText}
            </pre>
            <div className="flex gap-2">
              <button
                onClick={handleCopyLogs}
                className="flex items-center justify-center gap-1.5 flex-1 h-8 text-xs font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-full transition-colors"
              >
                {isCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                {isCopied ? 'Copied' : 'Copy Logs'}
              </button>
              <button
                onClick={handleSendLogs}
                disabled={isSending || isSent}
                className="flex items-center justify-center gap-1.5 flex-1 h-8 text-xs font-medium text-white bg-gray-900 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-full transition-colors"
              >
                {isSent ? <Check className="h-3 w-3" /> : <Send className="h-3 w-3" />}
                {isSent ? 'Sent to Support' : isSending ? 'Sending...' : 'Email to Support'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
